import React, {useCallback, useEffect, useRef} from "react";
import translate from "components/translations/translations.wrapper";
import {useDispatch} from "react-redux";
import {changeZoomPercentage} from "components/taskdetails-remake/taskDetails.action";
import _ from "lodash";
import {TextInput} from "components/common/TextInput.component";
import {Button, ButtonType} from "components/common/Button.component";
import {Maximize2, Plus, Minus} from "lucide-react";
import {ICON_SIZE} from "components/common/constants";

const MAX_ZOOM = 300;
const MIN_ZOOM = 50;

export const Menu = ({
                         totalImages,
                         rotateImage,
                         pageInView,
                         scrollToImage,
                         translate,
                         zoomPercentage,
                         changeZoom,
                         hasZoomToFit = true
                     }: any): React.ReactElement => {

    const downloadElement = useRef(null);

    const dispatch = useDispatch();

    const menuRef = React.useRef<HTMLDivElement>(null);
    const zoomInputRef = React.useRef<HTMLInputElement>(null);
    const pageInViewRef = React.useRef<HTMLInputElement>(null);

    const handlePageChange = _.debounce((event: React.ChangeEvent<HTMLInputElement>) => {
        let value = parseInt(event.target.value, 10);
        if (!value) return;
        if (value > 0 && value <= totalImages) {
            scrollToImage(value);
        }

        if (pageInViewRef.current)
            pageInViewRef.current.value = event.target.value;
    }, 300);

    const handleChangeZoom = (event: React.ChangeEvent<HTMLInputElement>) => {
        let value = parseInt(event.target.value, 10);
        if (!value) return;
        if (zoomInputRef.current)
            zoomInputRef.current.value = value.toString();

        if (value >= 50 && value <= 300) {
            dispatch(changeZoom(value));
        }
    }

    const handleZoomOut = () => {
        let newZoom = (zoomPercentage - 20 >= MIN_ZOOM) ? zoomPercentage - 20 : MIN_ZOOM;
        if (newZoom !== zoomPercentage) {
            dispatch(changeZoom(newZoom));
        }
    }

    const handleZoomIn = () => {
        let newZoom = (zoomPercentage + 20 <= MAX_ZOOM) ? zoomPercentage + 20 : MAX_ZOOM;
        if (newZoom !== zoomPercentage) {
            dispatch(changeZoom(newZoom));
        }
    }

    const handleZoomToFit = () => {
        dispatch(changeZoomPercentage(100));
    }

    useEffect(() => {
        if (zoomInputRef.current)
            zoomInputRef.current.value = Math.ceil(zoomPercentage).toString();
    }, [zoomPercentage, zoomInputRef]);

    useEffect(() => {
        if (pageInViewRef.current)
            pageInViewRef.current.value = pageInView.toString();
    }, [pageInView, pageInViewRef]);


    return (
        <div
            className={`attachments-panel__images__menu d-flex bg-white mt-8 px-16 align-items-center ${totalImages > 0 ? "justify-content-between" : "justify-content-center"}`}
            role={"menu"} ref={menuRef}>
            <a href="" target="_blank" download="" hidden={true} ref={downloadElement}></a>

            {totalImages > 0 &&
                <div className={"d-flex align-items-center"}>
                    <div className={"mr-2"}>{translate("taskDetail.panels.attachments.page")}</div>
                    <div className={"mx-2"}>
                        <TextInput
                            id={"page-in-view"}
                            type="number"
                            ref={pageInViewRef}
                            className={`page-in-view-input manual-zoom-input"}`}
                            onChange={handlePageChange}
                            min={1}
                            max={totalImages} step={1}/>
                    </div>
                    <span className={"mx-2"}>{translate("taskDetail.panels.attachments.of")}</span>
                    <span> {totalImages || 0}</span>
                </div>
            }


            <div className={"d-flex align-items-center"}>
                <span className={"mx-2"}>{translate("taskDetail.panels.attachments.zoom")}</span>

                <Button type={ButtonType.GHOST}
                        onClick={handleZoomOut}
                        iconOnly={true}
                        icon={<Minus size={ICON_SIZE.MEDIUM}/>}
                        ariaLabel={translate("taskDetail.zoomOut")}
                        tooltip={{id: "image-zoomout-tooltip", content: () => translate("taskDetail.zoomOut")}}
                />

                <div className={"mx-2"}>
                    <TextInput
                        id={"zoom-input"}
                        type="number"
                        ref={zoomInputRef}
                        className={`manual-zoom-input "}`}
                        onChange={useCallback(handleChangeZoom, [dispatch, changeZoom])}
                        min={MIN_ZOOM}
                        max={MAX_ZOOM} step={1}/>
                </div>

                <Button type={ButtonType.GHOST}
                        onClick={handleZoomIn}
                        iconOnly={true}
                        icon={<Plus size={ICON_SIZE.MEDIUM}/>}
                        ariaLabel={translate("taskDetail.zoomIn")}
                        tooltip={{id: "image-zoomin-tooltip", content: () => translate("taskDetail.zoomIn")}}
                />


            </div>

            <div className={"d-flex justify-content-between align-items-center"}>
                {rotateImage &&
                    <div className={"d-flex align-items-center"}>

                        <Button type={ButtonType.GHOST}
                                onClick={rotateImage}
                                iconOnly={false}
                                text={translate("taskDetail.rotateRight")}
                                tooltip={{id: "image-rotate-tooltip", content: () => translate("taskDetail.rotateRight")}}
                        />
                        {hasZoomToFit &&
                            <Button type={ButtonType.GHOST}
                                    onClick={handleZoomToFit}
                                    iconOnly={true}
                                    ariaLabel={translate("taskDetail.zoomToFit")}
                                    icon={<Maximize2 size={ICON_SIZE.MEDIUM}/>}
                                    dataTestId={"__tst_maximize"}
                                    tooltip={{id: "image-zoomtofit-tooltip", content: () => translate("taskDetail.zoomToFit")}}
                            />
                        }
                    </div>
                }
            </div>
        </div>
    )
}

export default translate(Menu);
